import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography, Container } from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import PropertyFooterLayout from '../../../components/properties/PropertyFooterLayout';

interface AddressDetails {
  country: string;
  nearbyLandmark?: string;
  streetAddress: string;
  province: string;
  county: string;
  district: string;
  postalCode: string;
  apartment: string;
  Zone?: string;
  neighborhood?: string
}

type PublishOutletContext = {
  handleNavigateToPropertyData: () => void;
  setAddressDetails: (details: {
    streetAddress: string;
  }) => void;
  addressDetails: AddressDetails;
  setPropertyCounty: (propertyCounty: string) => void;
  setPropertyDistrict: (propertyDistrict: string) => void;
};

const progress = 60; 
const step = 1; 

const AddressConfirmationPage: React.FC = () => {
  const navigate = useNavigate();

  const { addressDetails, setAddressDetails, setPropertyCounty, setPropertyDistrict, handleNavigateToPropertyData } = useOutletContext<PublishOutletContext>();

  const [formValues, setFormValues] = useState(addressDetails);
  const [readOnlyState, setReadOnlyState] = useState({
    country: true,
    nearbyLandmark: addressDetails.nearbyLandmark ? true : false,
    streetAddress: addressDetails.streetAddress.trim() !== '' ? true : false,
    apartment: addressDetails.apartment ? true : false,
    province: true,
    county: true,
    district: true,
    neighborhood: true,
    postalCode: true,
  });
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);

  useEffect(() => {
    setFormValues(addressDetails);
    setReadOnlyState({
      country: true,
      nearbyLandmark: addressDetails.nearbyLandmark ? true : false,
      streetAddress: addressDetails.streetAddress.trim() !== '' ? true : false,
      apartment: addressDetails.apartment ? true : false,
      province: true,
      county: true,
      district: true,
      neighborhood: true,
      postalCode: true,
    });
    setIsContinueDisabled(addressDetails.streetAddress.trim() === "");
  }, [addressDetails]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    if (name === 'streetAddress') {
      setIsContinueDisabled(value.trim() === "");
    }
  };
  

  const handleBack = () => {
    navigate(-1); 
  };

  const handleContinue = () => {
    setAddressDetails({
      ...addressDetails,
      streetAddress: formValues.streetAddress,
  })

    setPropertyCounty(formValues.county);
    setPropertyDistrict(formValues.district);

    handleNavigateToPropertyData();
    
  };

  return (
    <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', pb:14 , minHeight: '100vh' }}>
      <Box mt={2} p={2} sx={{ borderRadius: 1, boxShadow: 3, flexGrow: 1}}>
        <Typography variant="h5" gutterBottom mb={3}>
          Confirma tu dirección
        </Typography>
        <Box mb={2}>
          <TextField
            label="País/región"
            variant="outlined"
            fullWidth
            value={formValues.country}
            InputProps={{ readOnly: readOnlyState.country }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Punto de referencia cercano (si lo hay)"
            variant="outlined"
            fullWidth
            value={formValues.nearbyLandmark || ''}
            InputProps={{ readOnly: readOnlyState.nearbyLandmark }}
            onChange={handleChange}
            name="nearbyLandmark"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Dirección"
            variant="outlined"
            fullWidth
            value={formValues.streetAddress}
            InputProps={{ readOnly: readOnlyState.streetAddress }}
            onChange={handleChange}
            name="streetAddress"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Apartamento, piso, edificio (si corresponde)"
            variant="outlined"
            fullWidth
            value={formValues.apartment || ''}
            InputProps={{ readOnly: readOnlyState.apartment }}
            onChange={handleChange}
            name="apartment"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Departamento/estado/provincia/región"
            variant="outlined"
            fullWidth
            value={formValues.province}
            InputProps={{ readOnly: readOnlyState.province }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Cantón"
            variant="outlined"
            fullWidth
            value={formValues.county}
            InputProps={{ readOnly: readOnlyState.county }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Distrito"
            variant="outlined"
            fullWidth
            value={formValues.district}
            InputProps={{ readOnly: readOnlyState.district }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Barrio"
            variant="outlined"
            fullWidth
            value={formValues.neighborhood || ""}
            InputProps={{ readOnly: readOnlyState.neighborhood }}
            name="neighborhood"
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Zona"
            variant="outlined"
            fullWidth
            value={formValues.Zone || ''}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Código postal"
            variant="outlined"
            fullWidth
            value={formValues.postalCode}
            InputProps={{ readOnly: readOnlyState.postalCode }}
            sx={{ mb: 2 }}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <PropertyFooterLayout 
          progress={progress}
          step={step} 
          onBack={handleBack} 
          onContinue={handleContinue} 
          isContinueDisabled={isContinueDisabled} 
        />
      </Box>
    </Container>
  );
};

export default AddressConfirmationPage;
